import React, { useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Button, ThemeProvider, Typography } from "@mui/material";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import generateRewampTheme from "../rewampTheme";
import { AppLayoutContext } from "../../contexts/AppLayoutContext";

// The boundary wraps the whole route tree, so a throw on a legacy-themed route
// would otherwise render this panel without the rewamp palette it reads from.
const ErrorPanel = ({ onRetry }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { fontSize, ourTheme } = useContext(AppLayoutContext) || {};
  const theme = useMemo(
    () => generateRewampTheme(fontSize, ourTheme),
    [fontSize, ourTheme],
  );

  return (
    <ThemeProvider theme={theme}>
      <Box
        component="main"
        role="alert"
        sx={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: (theme) => theme.palette.layout.pagePadding,
          py: 6,
          backgroundColor: "background.default",
        }}
      >
        <Box
          sx={{
            maxWidth: "492px",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            textAlign: "center",
            backgroundColor: "background.paper",
            borderRadius: "16px",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: (theme) => theme.palette.layout.cardShadow,
            p: (theme) => theme.palette.layout.cardPadding,
          }}
        >
          <ReportProblemOutlinedIcon
            aria-hidden="true"
            sx={{ fontSize: 48, color: "neutral.nonInteractive" }}
          />
          <Typography
            variant="h2"
            component="h1"
            sx={{ color: "neutral.headings" }}
          >
            {t("REWAMP_ERROR_BOUNDARY_TITLE")}
          </Typography>
          <Typography variant="body1" sx={{ color: "neutral.body" }}>
            {t("REWAMP_ERROR_BOUNDARY_BODY")}
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, width: "100%", pt: 1 }}>
            <Button variant="secondary" sx={{ flex: 1 }} onClick={onRetry}>
              {t("REWAMP_ERROR_BOUNDARY_RETRY")}
            </Button>
            <Button
              variant="primary"
              sx={{ flex: 1 }}
              onClick={() => {
                onRetry();
                navigate(`${window.contextPath || ""}/`);
              }}
            >
              {t("DFSWEB_HOME")}
            </Button>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

ErrorPanel.propTypes = { onRetry: PropTypes.func.isRequired };

class ErrorBoundaryInner extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
    this.reset = this.reset.bind(this);
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.reset();
    }
  }

  componentDidCatch(error, info) {
    console.error("[RewampErrorBoundary]", error, info?.componentStack);
  }

  reset() {
    this.setState({ hasError: false });
  }

  render() {
    if (this.state.hasError) return <ErrorPanel onRetry={this.reset} />;
    return this.props.children;
  }
}

ErrorBoundaryInner.propTypes = {
  children: PropTypes.node,
  /** Changing this clears a caught error — fed the current pathname. */
  resetKey: PropTypes.string,
};

/**
 * Without this, any render-time throw unmounts the whole tree and the user gets
 * a blank white page — there was no boundary anywhere in src/.
 */
const RewampErrorBoundary = ({ children }) => {
  const { pathname } = useLocation();
  return (
    <ErrorBoundaryInner resetKey={pathname}>{children}</ErrorBoundaryInner>
  );
};

RewampErrorBoundary.propTypes = { children: PropTypes.node };

export default RewampErrorBoundary;
